
import React, { useState } from 'react';
import { Calculator, Euro, Clock, TrendingUp, Users } from 'lucide-react';

const ROICalculator: React.FC = () => {
  const [employees, setEmployees] = useState(5);
  const [hoursPerWeek, setHoursPerWeek] = useState(10);
  const [hourlyRate, setHourlyRate] = useState(75); // Start value updated
  
  // Calculation
  const hoursSavedPerYear = employees * hoursPerWeek * 48; // 48 weeks
  const moneySaved = hoursSavedPerYear * hourlyRate;
  const softwareCost = 299 * 12; // Example cost per year
  const roi = ((moneySaved - softwareCost) / softwareCost) * 100;

  const Slider = ({ label, value, min, max, unit, onChange, icon: Icon }: any) => (
    <div className="mb-8">
      <div className="flex justify-between mb-4">
        <label className="text-sm font-medium text-slate-400 flex items-center">
            <Icon className="w-4 h-4 mr-2 text-blue-500" /> {label}
        </label>
        <span className="text-white font-bold bg-slate-800 px-3 py-1 rounded-lg border border-slate-700">
            {value} <span className="text-slate-500 text-xs ml-1">{unit}</span>
        </span>
      </div>
      <input 
        type="range" 
        min={min} 
        max={max} 
        value={value} 
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-blue-600 hover:accent-blue-500"
      />
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
        <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-white mb-2">ROI Kalkulator</h2>
            <p className="text-slate-400">Berechnen Sie, wie schnell sich Orbit Software amortisiert.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-slate-900/50 border border-slate-800 p-8 rounded-2xl backdrop-blur-sm">
                <Slider 
                    label="Mitarbeiter im Admin/Service" 
                    value={employees} 
                    min={1} max={50} 
                    unit="Pers." 
                    onChange={setEmployees}
                    icon={Users}
                />
                <Slider 
                    label="Manuelle Stunden / Woche (pro Person)" 
                    value={hoursPerWeek} 
                    min={1} max={40} 
                    unit="Std." 
                    onChange={setHoursPerWeek}
                    icon={Clock}
                />
                <Slider 
                    label="Durchschn. Stundensatz" 
                    value={hourlyRate} 
                    min={50} max={300} 
                    unit="€" 
                    onChange={setHourlyRate}
                    icon={Euro}
                />
            </div>

            <div className="bg-gradient-to-br from-blue-900/20 to-slate-900 border border-blue-500/20 p-8 rounded-2xl flex flex-col justify-center relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 blur-[60px] rounded-full pointer-events-none"></div>
                
                <div className="mb-8 text-center">
                    <p className="text-slate-400 text-sm font-medium uppercase tracking-wider mb-2">Jährliche Ersparnis</p>
                    <div className="text-5xl font-bold text-white tracking-tight flex items-center justify-center">
                        <span className="text-blue-500 mr-2">€</span>
                        {moneySaved.toLocaleString()}
                    </div>
                </div>

                <div className="space-y-4">
                    <div className="flex justify-between items-center p-4 bg-slate-950/50 rounded-xl border border-slate-800">
                        <span className="text-slate-400 text-sm">Eingesparte Arbeitsstunden</span>
                        <span className="text-white font-mono font-bold">{hoursSavedPerYear.toLocaleString()} h</span>
                    </div>
                    <div className="flex justify-between items-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
                        <span className="text-emerald-400 text-sm flex items-center"><TrendingUp className="w-4 h-4 mr-2" /> Return on Invest</span>
                        <span className="text-emerald-400 font-mono font-bold text-xl">{roi.toFixed(0)}%</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
  );
};

export default ROICalculator;
